<template>
  <div class="profile-page">
    <section class="profile-header">
      <span>Perfil de acesso</span>
      <h1>Selecionar local</h1>
      <p>Escolha qual tenant deseja acessar com este número de celular.</p>
    </section>

    <button v-for="profile in profiles" :key="profile.id" class="profile-card" type="button" @click="select(profile)">
      <div class="profile-icon"><img src="/img/boarua-icon-concept-v1.png" alt="" /></div>
      <div>
        <strong>{{ profile.association && profile.association.name }}</strong>
        <span>{{ profile.street }}, {{ profile.number }}</span>
      </div>
      <v-icon color="primary">mdi-chevron-right</v-icon>
    </button>

    <section v-if="pendingInvites.length" class="pending-section">
      <span>Convites pendentes</span>
      <article v-for="invite in pendingInvites" :key="invite.id" class="pending-card">
        <div>
          <strong>{{ invite.association && invite.association.name }}</strong>
          <p>{{ invite.street }}, {{ invite.number }}</p>
          <small>Ao aceitar, sua residência será ativada e poderá receber mensalidades.</small>
        </div>
        <div class="pending-actions">
          <v-btn small text color="error" :loading="rejectingId === invite.id" :disabled="acceptingId === invite.id" @click="rejectInvite(invite)">
            Recusar
          </v-btn>
          <v-btn small depressed color="primary" :loading="acceptingId === invite.id" :disabled="rejectingId === invite.id" @click="acceptInvite(invite)">
            Aceitar
          </v-btn>
        </div>
      </article>
    </section>

    <section v-if="!loading && !profiles.length && !pendingInvites.length" class="empty-state">
      <v-icon color="primary" size="34">mdi-home-alert-outline</v-icon>
      <strong>Nenhuma associação vinculada</strong>
      <p>Você possui uma conta BOARUA, mas ainda não tem vínculo com uma associação. Aguarde um convite do administrador do local para usar a plataforma.</p>
    </section>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "BoaruaProfileSelect",
  data() {
    return { profiles: [], pendingInvites: [], loading: false, acceptingId: null, rejectingId: null };
  },
  async mounted() {
    await this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const [profilesResponse, pendingResponse] = await Promise.all([
          axios.get("/association-profiles"),
          axios.get("/association-invites/pending"),
        ]);
        this.profiles = profilesResponse.data.content || [];
        this.pendingInvites = this.uniquePendingInvites(pendingResponse.data.content || []);
      } finally {
        this.loading = false;
      }
    },
    uniquePendingInvites(invites = []) {
      const ranked = invites.slice().sort((a, b) => {
        const aLinked = a.member_unit_id ? 1 : 0;
        const bLinked = b.member_unit_id ? 1 : 0;
        if (aLinked !== bLinked) return bLinked - aLinked;
        return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
      });
      const seen = new Set();

      return ranked.filter((invite) => {
        const key = [
          invite.tenant_id,
          invite.association_id,
          invite.contribution_group_id || "",
          this.normalizeDigits(invite.phone) || this.normalizeText(invite.email),
          this.normalizeText(invite.street || invite.address_normalized),
          this.normalizeText(invite.number),
          this.normalizeText(invite.complement),
          this.normalizeText(invite.zip_code),
        ].join("|");

        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    normalizeText(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9]+/g, " ")
        .toLowerCase()
        .trim();
    },
    normalizeDigits(value) {
      return String(value || "").replace(/\D/g, "");
    },
    async acceptInvite(invite) {
      this.acceptingId = invite.id;
      try {
        await axios.post(`/association-invites/pending/${invite.id}/accept`);
        this.$dialog.notify.success("Convite aceito. Residência vinculada com sucesso.", { position: "top-right", timeout: 4000 });
        await this.load();
      } catch (error) {
        this.$dialog.notify.error(this.$codes ? this.$codes(error) : "Não foi possível aceitar o convite", { position: "top-right", timeout: 5000 });
      } finally {
        this.acceptingId = null;
      }
    },
    async rejectInvite(invite) {
      this.rejectingId = invite.id;
      try {
        await axios.post(`/association-invites/pending/${invite.id}/reject`);
        this.$dialog.notify.success("Convite recusado.", { position: "top-right", timeout: 3000 });
        await this.load();
      } catch (error) {
        this.$dialog.notify.error(this.$codes ? this.$codes(error) : "Não foi possível recusar o convite", { position: "top-right", timeout: 5000 });
      } finally {
        this.rejectingId = null;
      }
    },
    select(profile) {
      localStorage.setItem("associationProfile", JSON.stringify(profile));
      this.$router.push("/home");
    },
  },
};
</script>

<style scoped lang="scss">
.profile-page {
  min-height: 100%;
  padding: 0 16px 18px;
  color: #111827;
}

.profile-card {
  background: #ffffff;
  border: 1px solid #dfe7df;
  border-radius: 8px;
}

.profile-icon {
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }
}

.pending-section {
  margin-top: 18px;

  > span {
    color: #057a3d;
    font-size: 12px;
    font-weight: 900;
    text-transform: uppercase;
  }
}

.pending-card {
  margin-top: 8px;
  padding: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  border: 1px solid #dfe7df;
  border-radius: 8px;

  div {
    flex: 1;
    min-width: 0;
  }

  strong,
  p,
  small {
    display: block;
  }

  p {
    margin: 3px 0;
    color: #66736b;
    font-size: 13px;
  }

  small {
    color: #4b5f55;
    font-size: 12px;
    line-height: 1.35;
  }
}

.pending-actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

@media (max-width: 520px) {
  .pending-card {
    align-items: stretch;
    flex-direction: column;
  }

  .pending-actions {
    justify-content: flex-end;
  }
}

.empty-state {
  margin-top: 24px;
  padding: 22px;
  text-align: center;
  background: #ffffff;
  border: 1px solid #dfe7df;
  border-radius: 8px;

  strong {
    display: block;
    margin-top: 8px;
    color: #111827;
  }

  p {
    margin: 8px 0 0;
    color: #66736b;
    font-size: 13px;
    line-height: 1.45;
  }
}

.profile-header {
  margin: 0 -16px 14px;
  padding: 18px 16px;
  background: #ffffff;
  border-bottom: 1px solid #dfe7df;

  span {
    color: #057a3d;
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
  }

  h1 {
    margin: 7px 0 5px;
    font-size: 23px;
  }

  p {
    margin: 0;
    color: #66736b;
    font-size: 13px;
  }
}

.profile-card {
  width: 100%;
  min-height: 76px;
  margin-bottom: 8px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #111827;
  text-align: left;

  div:nth-child(2) {
    flex: 1;
    min-width: 0;
  }

  strong,
  span {
    display: block;
  }

  span {
    margin-top: 3px;
    color: #66736b;
    font-size: 13px;
  }
}

.profile-icon {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eef7f1;
  border-radius: 8px;
}
</style>
